import Component from '@glimmer/component';
import { service } from '@ember/service';
import ContactDuplicateModel from 'donor-elf-web/models/contact-duplicate';
import ContactsCache from 'donor-elf-web/services/contacts-cache';
import { on } from '@ember/modifier';
import ModalFooter from 'donor-elf-web/components/modal/footer';
import AlertInfo from 'donor-elf-web/components/alert/info';
import GenericError from 'donor-elf-web/components/generic-error';
import type ContactModel from 'donor-elf-web/models/contact';
import type Settings from 'donor-elf-web/services/settings';
import { LinkTo } from '@ember/routing';
import { tracked } from '@glimmer/tracking';
import type Store from '@ember-data/store';
import type AddSomethingModal from 'donor-elf-web/services/add-something-modal';
import type RouterService from '@ember/routing/router-service';
import type { ContactsMergedArgs } from 'donor-elf-web/services/add-something-modal';

export interface OnMergedArgs {
  contactDuplicate?: ContactDuplicateModel;
  contactIdKept: string;
}

// either @contactDuplicate or @contactIds should be passed in but not both
// @contactDuplicate is passed when processing that record
// @contactIds is pass when the user is manually merging contacts
interface ComponentArgs {
  contactDuplicate?: ContactDuplicateModel;
  contactIds?: string[];
  keepContactId?: string; // optional. Contact id to keep. Used when @contactIds is present
  onMerged?: (args: OnMergedArgs) => void;
  onNotMerged?: (args: { contactDuplicate?: ContactDuplicateModel }) => void;
  onRecordDestroyedConfirmed?: () => void;
}

export default class MergeContactAskComponent extends Component<ComponentArgs> {
  @service declare addSomethingModal: AddSomethingModal;
  @service declare contactsCache: ContactsCache;
  @service declare router: RouterService;
  @service declare settings: Settings;
  @service declare store: Store;

  @tracked processing = false;
  @tracked cantMergeContactsBecauseBothAssociatedWithAnEntity = false;
  @tracked mergingInModal = false;
  @tracked error?: any;

  contactsMergedSubscriptionToken?: string;
  addSomethingModalClosedSubscriptionToken?: string;

  get contacts(): ContactModel[] {
    return (
      this.args.contactIds ||
      this.args.contactDuplicate?.contactIds ||
      []
    ).map((contactId) => {
      return this.contactsCache.contactsById[contactId];
    });
  }

  get tryingToMergeWithAnonymousContact() {
    return this.contacts.some((x) => x?.anonymousDonor);
  }

  handleYesClicked = async () => {
    this.processing = true;
    if (this.settings.fundSettings?.cantMergeContactsWithDifferentEntities) {
      const contactEntityRecords = (
        await this.store.query('contact-entity', {
          filter: { contact_id: this.contacts.map((x) => x.id) },
        })
      ).slice();

      this.cantMergeContactsBecauseBothAssociatedWithAnEntity = (
        contactEntityRecords.every((x) => x.associatedWithEntities)
      )
    }
    this.processing = false;
    if (!this.cantMergeContactsBecauseBothAssociatedWithAnEntity) {
      this.mergingInModal = true;
      this.contactsMergedSubscriptionToken = this.addSomethingModal.subscribeToContactsMerged(this.handleContactsMerged)
      this.addSomethingModalClosedSubscriptionToken = this.addSomethingModal.subscribeToClose(() => {
        this.mergingInModal = false;
        this.addSomethingModal.unsubscribeWithToken(
          this.addSomethingModalClosedSubscriptionToken!
        );
        this.addSomethingModalClosedSubscriptionToken = undefined;
      });

      this.addSomethingModal.open({
        type: 'mergeContacts',
        mergeContactsOptions: {
          contactIds: this.contacts.map((x) => x.id)
        }
      });
    }
  }

  handleContactsMerged = (_message: string, data: ContactsMergedArgs) => {
    if (this.args.onMerged) {
      this.args.onMerged({
        contactDuplicate: this.args.contactDuplicate,
        contactIdKept: data.contactIdKept,
      });
    } else {
      this.router.transitionTo('funds.fund.contacts.contact', data.contactIdKept);
    }
  }

  handleDontMerge = async () => {
    this.processing = true;
    let { contactDuplicate } = this.args;
    if (contactDuplicate) {
      try {
        contactDuplicate.rejected = true;
        await contactDuplicate!.save();
        this.args.onNotMerged?.({
          contactDuplicate
        });
        this.processing = false;
      } catch (error) {
        this.error = error;
      }
    } else {
      this.args.onNotMerged?.({ contactDuplicate: undefined });
    }
  }

  willDestroy() {
    if (this.contactsMergedSubscriptionToken) {
      this.addSomethingModal.unsubscribeWithToken(
        this.contactsMergedSubscriptionToken
      );
    }
    if (this.addSomethingModalClosedSubscriptionToken) {
      this.addSomethingModal.unsubscribeWithToken(
        this.addSomethingModalClosedSubscriptionToken
      );
    }
  }

  <template>
    <div ...attributes class='border rounded tw-shadow-md'>
      {{#if (has-block 'header')}}
        <div class='tw-tracking-wide bg-gray-100 rounded-top py-2 px-3 border-bottom'>
          {{yield to='header'}}
        </div>
      {{/if}}
      {{#if this.tryingToMergeWithAnonymousContact}}
        <div class='p-3'>
          Sorry, you can't merge the <span class='tw-font-semibold'>Anonymous
          Donors</span> contact with another contact.
        </div>
        <ModalFooter>
          <button
            {{on 'click' @onNotMerged}}
            class='btn btn-xs btn-warning'
            type='button'
          >
            OK
          </button>
        </ModalFooter>
      {{else}}
        <div class='px-3 pt-2 pb-3 tw-grid tw-grid-cols-2 tw-gap-x-4'>
          {{#each this.contacts as |contact|}}
            <LinkTo @route='funds.fund.contacts.contact' @model={{contact.id}}>
              {{contact.name}}
            </LinkTo>
          {{/each}}
          {{#each this.contacts as |contact|}}
            <div class='tw-text-sm tw-leading-tight'>
              <div>{{contact.address1}}</div>
              <div>{{contact.address2}}</div>
              {{#if contact.cityAndState}}
                <div>{{contact.cityAndState}} {{contact.zip}}</div>
              {{/if}}
            </div>
          {{/each}}
          {{#each this.contacts as |contact|}}
            <div class='tw-text-sm'>
              {{#if contact.hasEmails}}
                <div class='mt-2'>
                  {{#each contact.emails.data as |item|}}
                    <div>{{item.v}}</div>
                  {{/each}}
                </div>
              {{/if}}
            </div>
          {{/each}}
        </div>
        {{#if this.cantMergeContactsBecauseBothAssociatedWithAnEntity}}
          <div class='px-3 pb-3'>
            <AlertInfo>
              Sorry, you can't merge these contacts because they're both associated with
              a different donor record.
            </AlertInfo>
          </div>
        {{/if}}
        {{#if this.error}}
          <GenericError
            @error={{this.error}}
            @useLeftBorderCard={{true}}
            class='m-3 tw-text-sm'
          />
        {{/if}}
        <ModalFooter class='d-flex align-items-baseline tw-gap-x-3 tw-min-h-10'>
          {{#if this.processing}}
            <div><i>Processing...</i></div>
          {{else if this.cantMergeContactsBecauseBothAssociatedWithAnEntity}}
            <button
              {{on 'click' @onNotMerged}}
              class='d-block btn btn-xs btn-warning'
              type='button'
            >
              Cancel
            </button>
          {{else if this.mergingInModal}}
            <div><i>Merging...</i></div>
          {{else}}
            <div>
              Merge these contacts?
            </div>
            <button
              {{on 'click' this.handleYesClicked}}
              class='d-block btn btn-xs btn-warning'
              type='button'
            >
              Yes
            </button>
            <button
              {{on 'click' this.handleDontMerge}}
              class='d-block btn btn-xs btn-outline-secondary'
              type='button'
            >
              No
            </button>
          {{/if}}
        </ModalFooter>
      {{/if}}
    </div>
  </template>
}
